/** The field layout for wiring one activity's export to another's trigger, new or rebound. */

import { useState } from "react";
import type { EventBinding } from "@nebulize/sdk";

import { Button, EmptyState, Field, Select, Stack, StatusBadge, Toolbar } from "@/components/ui";

import { Muted } from "../components/Text";
import type { SpaceActivities } from "../spaces/lib/queries";
import type { AddedActivityRow, CatalogEntry } from "../types";

import { sourcesAmong, targetsAmong, unfilledRequiredParams, wouldCycle } from "./lib/wires";

export interface WireFormValue {
  readonly source: string;
  readonly export: string;
  readonly target: string;
  readonly trigger: string;
  readonly parameters: Record<string, string>;
}

export interface WireFormProps {
  readonly activities: SpaceActivities;
  /** Every wire already on this space, for the cycle check. */
  readonly wires: readonly EventBinding[];
  /** A rebind: neither end moves, so no end picker is offered. */
  readonly locked?: { readonly source: string; readonly target: string };
  /** The wire this form itself edits, excluded from its own cycle check. */
  readonly excludeBindingId?: string;
  readonly initialExport?: string;
  readonly initialTrigger?: string;
  readonly submitLabel: string;
  readonly pending: boolean;
  readonly errorMessage?: string | undefined;
  readonly onSubmit: (value: WireFormValue) => void;
  readonly onCancel: () => void;
}

export function WireForm({
  activities,
  wires,
  locked,
  excludeBindingId,
  initialExport,
  initialTrigger,
  submitLabel,
  pending,
  errorMessage,
  onSubmit,
  onCancel,
}: WireFormProps) {
  const entryFor = (row: AddedActivityRow): CatalogEntry | undefined => activities.entryFor(row);
  const sources = sourcesAmong(activities.rows, entryFor);
  const targets = targetsAmong(activities.rows, entryFor);

  const [source, setSource] = useState(locked?.source ?? sources[0]?.row.added_activity_id ?? "");
  const [exported, setExported] = useState(
    initialExport ??
      sources.find((one) => one.row.added_activity_id === source)?.exports[0]?.name ??
      "",
  );
  const [target, setTarget] = useState(locked?.target ?? "");
  const [trigger, setTrigger] = useState(initialTrigger ?? "");
  /** One export field name per declared trigger parameter. Empty means matched by name. */
  const [mapping, setMapping] = useState<Record<string, string>>({});

  const sourceEntry = sources.find((one) => one.row.added_activity_id === source);
  const targetEntry = targets.find((one) => one.row.added_activity_id === target);
  const chosenTrigger = targetEntry?.triggers.find((one) => one.name === trigger);
  const chosenExport = sourceEntry?.exports.find((one) => one.name === exported);
  const fields = chosenExport?.fields ?? [];
  const missing = unfilledRequiredParams(chosenTrigger, fields, mapping);
  const cycles = wouldCycle(source, target, wires, excludeBindingId);

  if (locked === undefined && (sources.length === 0 || targets.length === 0)) {
    return (
      <EmptyState
        bare
        headline="Nothing to wire yet"
        body="A wire needs one activity that exports an event and another that accepts a trigger."
      />
    );
  }

  if (locked !== undefined && (sourceEntry === undefined || targetEntry === undefined)) {
    return <Muted small>Manifest unreadable for one end</Muted>;
  }

  const parameters = (): Record<string, string> => {
    const out: Record<string, string> = {};
    for (const [param, field] of Object.entries(mapping)) {
      if (field !== "" && fields.includes(field)) out[param] = `{${field}}`;
    }
    return out;
  };

  return (
    <Stack gap={3}>
      {locked !== undefined ? null : (
        <Field label="When this activity">
          <Select
            value={source}
            options={sources.map((one) => ({
              value: one.row.added_activity_id,
              label: one.row.name,
            }))}
            onChange={(event) => {
              const next = event.target.value;
              setSource(next);
              setExported(
                sources.find((one) => one.row.added_activity_id === next)?.exports[0]?.name ?? "",
              );
              setMapping({});
            }}
          />
        </Field>
      )}
      <Field label="sends">
        <Select
          value={exported}
          options={(sourceEntry?.exports ?? []).map((one) => ({
            value: one.name,
            label: one.name,
          }))}
          onChange={(event) => {
            setExported(event.target.value);
            setMapping({});
          }}
        />
      </Field>
      {locked !== undefined ? null : (
        <Field label="make this activity">
          <Select
            value={target}
            options={[
              { value: "", label: "pick one" },
              ...targets
                .filter((one) => one.row.added_activity_id !== source)
                .map((one) => ({ value: one.row.added_activity_id, label: one.row.name })),
            ]}
            onChange={(event) => {
              const next = event.target.value;
              setTarget(next);
              setTrigger(
                targets.find((one) => one.row.added_activity_id === next)?.triggers[0]?.name ?? "",
              );
              setMapping({});
            }}
          />
        </Field>
      )}
      <Field label="do">
        <Select
          value={trigger}
          options={(targetEntry?.triggers ?? []).map((one) => ({
            value: one.name,
            label: one.name,
          }))}
          onChange={(event) => {
            setTrigger(event.target.value);
            setMapping({});
          }}
        />
      </Field>

      {chosenTrigger === undefined ? null : chosenTrigger.params.length === 0 ? (
        <Muted small>Nothing to fill in</Muted>
      ) : (
        <Stack gap={3}>
          {chosenTrigger.params.map((param) => (
            <Field
              key={param.name}
              label={param.required ? `${param.name} (required)` : param.name}
              description={`${param.type}, filled from ${exported}`}
              error={missing.includes(param.name) ? "Not filled" : undefined}
            >
              <Select
                value={mapping[param.name] ?? ""}
                options={[
                  {
                    value: "",
                    label: fields.includes(param.name)
                      ? `${param.name} (matched by name)`
                      : "not filled",
                  },
                  ...fields.map((one) => ({ value: one, label: one })),
                ]}
                onChange={(event) => {
                  const chosen = event.target.value;
                  setMapping((held) => ({ ...held, [param.name]: chosen }));
                }}
              />
            </Field>
          ))}
        </Stack>
      )}

      {cycles ? <StatusBadge tone="danger">would cycle</StatusBadge> : null}
      {errorMessage === undefined ? null : <StatusBadge tone="danger">{errorMessage}</StatusBadge>}

      <Toolbar>
        <Button
          size="sm"
          variant="primary"
          loading={pending}
          disabled={
            source === "" ||
            target === "" ||
            exported === "" ||
            trigger === "" ||
            missing.length > 0 ||
            cycles
          }
          onClick={() => {
            onSubmit({ source, export: exported, target, trigger, parameters: parameters() });
          }}
        >
          {submitLabel}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </Toolbar>
    </Stack>
  );
}
